import React, { useEffect, useMemo, useState } from "react"
import styles from "./whatsAppApiIntegration.module.css"
import api from "../../services/api"
import { toast } from "react-toastify"
import { MdAdd, MdCall, MdEdit, MdOpenInNew, MdOutlineBusiness, MdOutlineCheckCircle, MdOutlineDeleteOutline, MdOutlineHistory, MdOutlineInfo, MdOutlineMail, MdOutlinePlace, MdOutlinePublic, MdOutlineRefresh, MdOutlineShare, MdOutlineWarningAmber } from "react-icons/md"
import Button from "../../components/Form/Button"
import CharacterCounter from "../../components/Form/CharacterCounter"
import FormGroup from "../../components/Form/FormGroup"
import FormGroupInline from "../../components/Form/FormGroupInline"
import Input from "../../components/Form/Input"
import InputFile from "../../components/Form/InputFile"
import Select from "../../components/Form/Select"
import Textarea from "../../components/Form/Textarea"
import InfoTooltip from "../../components/InfoTooltip"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import { maskPhone } from "../../utils/formatter"
import { openLinkExternal } from "../../utils/utils"
import { useWebSocketChatContext } from "../../contexts/webSocketChatContext"

const verticalOptions = [
    { id: "AUTO", value: "Automotivo" },
    { id: "BEAUTY", value: "Beleza" },
    { id: "APPAREL", value: "Vestuário" },
    { id: "EDU", value: "Educação" },
    { id: "ENTERTAIN", value: "Entretenimento" },
    { id: "EVENT_PLAN", value: "Eventos" },
    { id: "FINANCE", value: "Finanças" },
    { id: "GROCERY", value: "Mercado" },
    { id: "GOVT", value: "Governo" },
    { id: "HOTEL", value: "Hotelaria" },
    { id: "HEALTH", value: "Saúde" },
    { id: "NONPROFIT", value: "Sem fins lucrativos" },
    { id: "PROF_SERVICES", value: "Serviços profissionais" },
    { id: "RETAIL", value: "Varejo" },
    { id: "TRAVEL", value: "Viagem" },
    { id: "RESTAURANT", value: "Restaurante" },
    { id: "OTHER", value: "Outro" },
]

const defaultProfile = {
    about: "",
    address: "",
    description: "",
    email: "",
    websites: [],
    vertical: "OTHER",
}
const defaultCallingSettings = {
    status: "DISABLED",
    call_icon_visibility: "DEFAULT",
    callback_permission_status: "DISABLED",
    call_hours: {
        status: "DISABLED",
        timezone_id: "America/Sao_Paulo",
        weekly_operating_hours: [],
        holiday_schedule: [],
    },
    sip: { status: "DISABLED", servers: [] },
    restrictions: { restrictions_list: [] },
}
const weekDays = [
    { id: "MONDAY", label: "Segunda-feira" },
    { id: "TUESDAY", label: "Terça-feira" },
    { id: "WEDNESDAY", label: "Quarta-feira" },
    { id: "THURSDAY", label: "Quinta-feira" },
    { id: "FRIDAY", label: "Sexta-feira" },
    { id: "SATURDAY", label: "Sábado" },
    { id: "SUNDAY", label: "Domingo" },
]
const formatCallingTime = value => {
    const normalized = `${value || ""}`.replace(/\D/g, "").padStart(4, "0").slice(-4)
    return `${normalized.slice(0, 2)}:${normalized.slice(2)}`
}
const normalizeCallingSettings = data => ({
    ...defaultCallingSettings,
    ...(data || {}),
    call_hours: {
        ...defaultCallingSettings.call_hours,
        ...(data?.call_hours || {}),
        weekly_operating_hours: (data?.call_hours?.weekly_operating_hours || []).map(item => ({
            ...item,
            open_time: formatCallingTime(item.open_time),
            close_time: formatCallingTime(item.close_time),
        })),
        holiday_schedule: (data?.call_hours?.holiday_schedule || []).filter(item => item?.date >= new Date().toISOString().slice(0, 10)).map(item => ({
            ...item,
            start_time: formatCallingTime(item.start_time),
            end_time: formatCallingTime(item.end_time),
        })),
    },
    sip: { ...defaultCallingSettings.sip, ...(data?.sip || {}) },
    restrictions: { ...defaultCallingSettings.restrictions, ...(data?.restrictions || {}) },
})
const blankProfilePictureSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
        <rect width="640" height="640" fill="#d9dde3"/>
        <path fill="#9aa0a6" d="M320 197.6v-62.9H162.7v330.6h314.6V197.6H320zM225.6 433.9h-31.5v-31.5h31.5v31.5zm0-62.9h-31.5v-31.5h31.5V371zm0-62.9h-31.5v-31.5h31.5v31.5zm0-62.9h-31.5v-31.5h31.5v31.5zm0-62.9h-31.5v-31.5h31.5v31.5zm62.9 251.6H257v-31.5h31.5v31.5zm0-62.9H257v-31.5h31.5V371zm0-62.9H257v-31.5h31.5v31.5zm0-62.9H257v-31.5h31.5v31.5zm0-62.9H257v-31.5h31.5v31.5zm157.3 251.6H320v-31.5h31.5V371H320v-31.5h31.5V308H320v-31.5h31.5V245H320v-31.5h125.9v220.4zm-31.4-125.8h-31.5v31.5h31.5v-31.5zm0 62.9h-31.5v31.5h31.5V371z"/>
    </svg>
`
const blankProfilePictureUrl = `data:image/svg+xml;utf8,${encodeURIComponent(blankProfilePictureSvg)}`
const normalizeUsername = value => `${value || ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/^@+/, "")
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9._]/g, "")
    .replace(/^\.+/, "")
    .replace(/\.{2,}/g, ".")
    .slice(0, 35)
const isValidUsername = value => {
    const username = `${value || ""}`
    return username.length >= 3
        && username.length <= 35
        && /^[a-z0-9._]+$/.test(username)
        && /[a-z]/.test(username)
        && !username.endsWith(".")
        && !/^www/i.test(username)
        && !/\.(com|org|net|int|edu|gov|mil|us|in|html)$/i.test(username)
}
const generateUsernameSuggestions = value => {
    const words = `${value || ""}`
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
    if (!words.length) return []

    const joined = words.join("")
    const officialRoot = joined.slice(0, 27)
    return [...new Set([
        joined,
        words.join("_"),
        words.join("."),
        `${officialRoot}.oficial`,
    ].map(normalizeUsername))].filter(isValidUsername).slice(0, 4)
}

const PhoneNumberManager = ({ linkedAccounts = [], setIsLoading = () => { }, onUpdateAccount = () => { }, initialInboxId = "" }) => {
    const { lastJsonMessage } = useWebSocketChatContext()
    const [selectedInboxId, setSelectedInboxId] = useState("")
    const [phoneData, setPhoneData] = useState({})
    const [profile, setProfile] = useState(defaultProfile)
    const [displayName, setDisplayName] = useState("")
    const [displayNameDraft, setDisplayNameDraft] = useState("")
    const [usernameData, setUsernameData] = useState({ username: "", status: "", requested_username: "", suggestions: [] })
    const [usernameDraft, setUsernameDraft] = useState("")
    const [usernameError, setUsernameError] = useState("")
    const [profilePicture, setProfilePicture] = useState([])
    const [callingSettings, setCallingSettings] = useState(defaultCallingSettings)
    const [callingSettingsSnapshot, setCallingSettingsSnapshot] = useState(defaultCallingSettings)
    const [callingSettingsError, setCallingSettingsError] = useState("")
    const [isEditing, setIsEditing] = useState(false)
    const [isEditingDisplayName, setIsEditingDisplayName] = useState(false)
    const [isEditingUsername, setIsEditingUsername] = useState(false)
    const [isEditingProfilePicture, setIsEditingProfilePicture] = useState(false)
    const [isEditingCallingSettings, setIsEditingCallingSettings] = useState(false)
    const [showRemovePictureConfirm, setShowRemovePictureConfirm] = useState(false)
    const [showRemoveUsernameConfirm, setShowRemoveUsernameConfirm] = useState(false)
    const [snapshot, setSnapshot] = useState({ profile: defaultProfile })
    const [officialBusinessAccountUrl, setOfficialBusinessAccountUrl] = useState("")
    const [diagnostics, setDiagnostics] = useState({ health: [], official_business_account: [] })
    const [history, setHistory] = useState([])
    const [webhookHealth, setWebhookHealth] = useState(null)
    const [webhookLastReceivedAt, setWebhookLastReceivedAt] = useState(null)
    const [loaded, setLoaded] = useState(false)

    const profilePicturePreviewUrl = useMemo(() => {
        if (!profilePicture?.[0]) return ""
        return URL.createObjectURL(profilePicture[0])
    }, [profilePicture])

    const accountOptions = useMemo(() => {
        return linkedAccounts.map(account => ({
            id: account._id,
            value: `${account.name || account?.metadata?.verified_name || "Conta do WhatsApp"} - ${maskPhone(account?.metadata?.phone_number)}`,
        }))
    }, [linkedAccounts])

    const metaUsernameSuggestions = useMemo(() => [...new Set(usernameData.suggestions || [])], [usernameData.suggestions])
    const localUsernameSuggestions = useMemo(() => {
        if (metaUsernameSuggestions.length) return []
        const unavailable = new Set([usernameData.username, usernameData.requested_username].filter(Boolean))
        return generateUsernameSuggestions(displayName || phoneData?.verified_name).filter(suggestion => !unavailable.has(suggestion))
    }, [displayName, metaUsernameSuggestions.length, phoneData?.verified_name, usernameData.requested_username, usernameData.username])
    const usernameSuggestions = metaUsernameSuggestions.length ? metaUsernameSuggestions : localUsernameSuggestions

    useEffect(() => {
        if (!selectedInboxId && accountOptions.length > 0) {
            const initialAccount = accountOptions.find(account => `${account.id}` === `${initialInboxId}`)
            setSelectedInboxId(initialAccount?.id || accountOptions[0].id)
        }
    }, [accountOptions, initialInboxId, selectedInboxId])

    useEffect(() => {
        if (selectedInboxId) {
            getPhoneProfile(selectedInboxId)
        }
    }, [selectedInboxId])

    useEffect(() => {
        if (lastJsonMessage?.event !== "WHATSAPP_META_WEBHOOK") return
        if (`${lastJsonMessage?.content?.inbox_id || ""}` !== `${selectedInboxId || ""}`) return
        getPhoneProfile(selectedInboxId)
    }, [lastJsonMessage, selectedInboxId])

    useEffect(() => {
        return () => {
            if (profilePicturePreviewUrl) URL.revokeObjectURL(profilePicturePreviewUrl)
        }
    }, [profilePicturePreviewUrl])

    const getPhoneProfile = async (inboxId = selectedInboxId) => {
        try {
            if (!inboxId) return

            setIsLoading(true)
            const response = await api.get(`/integrations/whatsapp-api/accounts/${inboxId}/phone-profile`)
            const data = response?.data?.data || {}
            const businessProfile = data.business_profile || {}

            setPhoneData(data.phone_number || {})
            setDiagnostics(data.diagnostics || { health: [], official_business_account: [] })
            setHistory(data.history || [])
            setWebhookHealth(data.webhook_health || null)
            setWebhookLastReceivedAt(data.webhook_last_received_at || null)
            setOfficialBusinessAccountUrl(data.official_business_account_url || "")
            setDisplayName(data.phone_number?.verified_name || "")
            const nextProfile = {
                ...defaultProfile,
                ...businessProfile,
                websites: Array.isArray(businessProfile.websites) ? businessProfile.websites : [],
            }
            const nextDisplayName = data.phone_number?.verified_name || ""
            setDisplayName(nextDisplayName)
            setDisplayNameDraft(nextDisplayName)
            setProfile(nextProfile)
            setSnapshot({ profile: nextProfile })
            setProfilePicture([])
            setIsEditing(false)
            setIsEditingDisplayName(false)
            setIsEditingUsername(false)
            setIsEditingProfilePicture(false)
            setIsEditingCallingSettings(false)
            setLoaded(true)
            await Promise.all([getBusinessUsername(inboxId), getCallingSettings(inboxId)])
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const getCallingSettings = async (inboxId = selectedInboxId, showSuccessToast = false) => {
        try {
            if (!inboxId) return

            const response = await api.get(`/integrations/whatsapp-api/accounts/${inboxId}/calling-settings`)
            const nextSettings = normalizeCallingSettings(response?.data?.data || {})
            setCallingSettings(nextSettings)
            setCallingSettingsSnapshot(nextSettings)
            setCallingSettingsError("")
            if (showSuccessToast) toast.success("Configurações de ligação atualizadas.")
        } catch (err) {
            const message = err?.response?.data?.message || "As configurações de ligação não estão disponíveis para este número."
            setCallingSettings(defaultCallingSettings)
            setCallingSettingsSnapshot(defaultCallingSettings)
            setCallingSettingsError(message)
        }
    }

    const getBusinessUsername = async (inboxId = selectedInboxId, showErrorToast = false) => {
        try {
            if (!inboxId) return

            const response = await api.get(`/integrations/whatsapp-api/accounts/${inboxId}/username`)
            const data = response?.data?.data || {}
            const nextData = {
                username: normalizeUsername(data.username),
                status: data.status || "",
                requested_username: normalizeUsername(data.requested_username),
                suggestions: Array.isArray(data.suggestions) ? data.suggestions.map(normalizeUsername) : [],
            }
            setUsernameData(nextData)
            setUsernameDraft(nextData.requested_username || nextData.username || "")
            setUsernameError("")
        } catch (err) {
            const message = err?.response?.data?.message || "O recurso ainda não está disponível para este número."
            setUsernameData({ username: "", status: "", requested_username: "", suggestions: [] })
            setUsernameError(message)
            if (showErrorToast) toast.error(message)
        }
    }

    const handleSaveUsername = async (e) => {
        e?.preventDefault()

        try {
            setIsLoading(true)
            const response = await api.post(`/integrations/whatsapp-api/accounts/${selectedInboxId}/username`, { username: normalizeUsername(usernameDraft) })
            toast.success(response?.data?.message)
            setIsEditingUsername(false)
            await getBusinessUsername(selectedInboxId, true)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleConfirmRemoveUsername = async () => {
        try {
            setShowRemoveUsernameConfirm(false)
            setIsLoading(true)
            const response = await api.delete(`/integrations/whatsapp-api/accounts/${selectedInboxId}/username`)
            setUsernameData(previousValue => ({ ...previousValue, username: "", status: "", requested_username: "" }))
            setUsernameDraft("")
            setIsEditingUsername(false)
            toast.success(response?.data?.message)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleChangeProfile = (name, value) => {
        setProfile(previousValue => ({ ...previousValue, [name]: value }))
    }

    const handleChangeWebsite = (index, value) => {
        setProfile(previousValue => {
            const websites = [...(previousValue.websites || [])]
            websites[index] = value
            return { ...previousValue, websites }
        })
    }

    const handleSaveDisplayName = async (e) => {
        e?.preventDefault()

        try {
            setIsLoading(true)
            const response = await api.patch(`/integrations/whatsapp-api/accounts/${selectedInboxId}/display-name`, { display_name: displayNameDraft })
            const nextPhoneData = response?.data?.data?.phone_number || {}
            setPhoneData(nextPhoneData)
            setDisplayName(displayNameDraft)
            onUpdateAccount({ _id: selectedInboxId, name: displayNameDraft, metadata: { verified_name: nextPhoneData?.verified_name || displayNameDraft } })
            setIsEditingDisplayName(false)
            toast.success(response?.data?.message)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleSaveBusinessProfile = async (e) => {
        e?.preventDefault()

        try {
            setIsLoading(true)
            const response = await api.patch(`/integrations/whatsapp-api/accounts/${selectedInboxId}/business-profile`, {
                ...profile,
                websites: (profile.websites || []).filter(Boolean),
            })
            const nextProfile = response?.data?.data || {}
            const formattedProfile = { ...defaultProfile, ...nextProfile, websites: nextProfile.websites || [] }
            setProfile(formattedProfile)
            setSnapshot(previousValue => ({ ...previousValue, profile: formattedProfile }))
            toast.success(response?.data?.message)
            return true
        } catch (err) {
            toast.error(err?.response?.data?.message)
            return false
        } finally {
            setIsLoading(false)
        }
    }

    const handleChangeCallingSetting = (name, value) => {
        setCallingSettings(previousValue => ({ ...previousValue, [name]: value }))
    }

    const handleChangeCallHours = (name, value) => {
        setCallingSettings(previousValue => ({
            ...previousValue,
            call_hours: { ...previousValue.call_hours, [name]: value },
        }))
    }

    const handleAddWeeklyHours = (dayOfWeek) => {
        setCallingSettings(previousValue => {
            const weekly = previousValue.call_hours.weekly_operating_hours || []
            if (weekly.filter(item => item.day_of_week === dayOfWeek).length >= 2) return previousValue
            return {
                ...previousValue,
                call_hours: {
                    ...previousValue.call_hours,
                    weekly_operating_hours: [...weekly, { day_of_week: dayOfWeek, open_time: "08:00", close_time: "18:00" }],
                },
            }
        })
    }

    const handleChangeWeeklyHours = (index, name, value) => {
        setCallingSettings(previousValue => {
            const weekly = [...(previousValue.call_hours.weekly_operating_hours || [])]
            weekly[index] = { ...weekly[index], [name]: value }
            return { ...previousValue, call_hours: { ...previousValue.call_hours, weekly_operating_hours: weekly } }
        })
    }

    const handleRemoveWeeklyHours = (index) => {
        setCallingSettings(previousValue => ({
            ...previousValue,
            call_hours: {
                ...previousValue.call_hours,
                weekly_operating_hours: (previousValue.call_hours.weekly_operating_hours || []).filter((_, itemIndex) => itemIndex !== index),
            },
        }))
    }

    const handleAddHolidaySchedule = () => {
        setCallingSettings(previousValue => {
            const holidays = previousValue.call_hours.holiday_schedule || []
            if (holidays.length >= 20) return previousValue
            return {
                ...previousValue,
                call_hours: {
                    ...previousValue.call_hours,
                    holiday_schedule: [...holidays, { date: new Date().toISOString().slice(0, 10), start_time: "00:00", end_time: "23:59" }],
                },
            }
        })
    }

    const handleChangeHolidaySchedule = (index, name, value) => {
        setCallingSettings(previousValue => {
            const holidays = [...(previousValue.call_hours.holiday_schedule || [])]
            holidays[index] = { ...holidays[index], [name]: value }
            return { ...previousValue, call_hours: { ...previousValue.call_hours, holiday_schedule: holidays } }
        })
    }

    const handleRemoveHolidaySchedule = (index) => {
        setCallingSettings(previousValue => ({
            ...previousValue,
            call_hours: {
                ...previousValue.call_hours,
                holiday_schedule: (previousValue.call_hours.holiday_schedule || []).filter((_, itemIndex) => itemIndex !== index),
            },
        }))
    }

    const handleSaveCallingSettings = async () => {
        try {
            setIsLoading(true)
            const response = await api.patch(`/integrations/whatsapp-api/accounts/${selectedInboxId}/calling-settings`, { calling: callingSettings })
            const nextSettings = normalizeCallingSettings(response?.data?.data || callingSettings)
            setCallingSettings(nextSettings)
            setCallingSettingsSnapshot(nextSettings)
            setCallingSettingsError("")
            setIsEditingCallingSettings(false)
            toast.success(response?.data?.message)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível atualizar as configurações de ligação.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleCancelCallingSettings = () => {
        setCallingSettings(callingSettingsSnapshot)
        setIsEditingCallingSettings(false)
    }

    const handleSaveProfilePicture = async (e) => {
        e?.preventDefault()

        try {
            if (!profilePicture?.[0]) {
                toast.error("Selecione uma imagem.")
                return
            }

            setIsLoading(true)
            const formData = new FormData()
            formData.append("image", profilePicture[0])

            const response = await api.patch(`/integrations/whatsapp-api/accounts/${selectedInboxId}/profile-picture`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            })
            const nextProfile = response?.data?.data || {}
            const formattedProfile = { ...defaultProfile, ...nextProfile, websites: nextProfile.websites || [] }
            setProfile(formattedProfile)
            setSnapshot(previousValue => ({ ...previousValue, profile: formattedProfile }))
            setProfilePicture([])
            setIsEditingProfilePicture(false)
            toast.success(response?.data?.message)
            return true
        } catch (err) {
            toast.error(err?.response?.data?.message)
            return false
        } finally {
            setIsLoading(false)
        }
    }

    const handleOpenRemoveProfilePicture = () => {
        setShowRemovePictureConfirm(true)
    }

    const handleConfirmRemoveProfilePicture = async () => {
        try {
            setShowRemovePictureConfirm(false)
            setIsLoading(true)
            const response = await api.delete(`/integrations/whatsapp-api/accounts/${selectedInboxId}/profile-picture`)
            const nextProfile = response?.data?.data || {}
            const formattedProfile = { ...defaultProfile, ...nextProfile, websites: nextProfile.websites || [] }
            setProfile(formattedProfile)
            setSnapshot(previousValue => ({ ...previousValue, profile: formattedProfile }))
            setProfilePicture([])
            setIsEditingProfilePicture(false)
            toast.success(response?.data?.message)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const getStatusValue = (value) => value || "-"
    const getVerticalLabel = (value) => verticalOptions.find(option => option.id === value)?.value || "Outro"

    const getProfilePicturePreview = () => {
        return profilePicturePreviewUrl || profile.profile_picture_url || ""
    }

    const getProfilePictureFallback = () => {
        return getProfilePicturePreview() || blankProfilePictureUrl
    }

    const getPreviewWebsites = () => (profile.websites || []).filter(Boolean)

    const getOptionalLabel = (label) => `${label} · Opcional`

    const getUsernameStatusLabel = (status) => ({
        approved: "Aprovado e visível",
        active: "Ativo",
        reserved: "Reservado",
        pending: "Em análise",
        rejected: "Rejeitado",
        deleted: "Removido",
    }[`${status || ""}`.toLowerCase()] || status || "Sem nome de usuário")

    const labelWithInfo = (label, text) => (
        <span className={styles.labelWithInfo}>
            {label}
            <InfoTooltip text={text} preferredPosition="bottom" />
        </span>
    )

    const officialBadgeTooltip = (
        <div className={styles.officialTooltip}>
            <span className={styles.officialBadgePreview}>✓</span>
            <span>Selo que aparece ao lado do nome quando o WhatsApp confirma que a conta pertence a uma marca autêntica.</span>
        </div>
    )

    const renderDiagnosticItem = (item) => (
        <div key={item.id} className={`${styles.diagnosticItem} ${styles[`diagnostic_${item.status}`] || ""}`}>
            {item.status === "ok" || item.status === "done" ? <MdOutlineCheckCircle /> : <MdOutlineWarningAmber />}
            <div>
                <strong>{item.label}</strong>
                <span>{item.description}</span>
            </div>
        </div>
    )

    const handleStartEditing = () => {
        setSnapshot({ profile })
        setIsEditing(true)
    }

    const handleCancelEditing = () => {
        setProfile(snapshot.profile)
        setProfilePicture([])
        setIsEditing(false)
    }

    const handleStartEditingDisplayName = () => {
        setDisplayNameDraft(displayName)
        setIsEditingDisplayName(true)
    }

    const handleCancelEditingDisplayName = () => {
        setDisplayNameDraft(displayName)
        setIsEditingDisplayName(false)
    }

    const handleStartEditingProfilePicture = () => {
        setProfilePicture([])
        setIsEditingProfilePicture(true)
    }

    const handleCancelEditingProfilePicture = () => {
        setProfilePicture([])
        setIsEditingProfilePicture(false)
    }

    const handleSubmitProfilePicture = async (e) => {
        e.preventDefault()
        await handleSaveProfilePicture(e)
    }

    const handleSaveAll = async (e) => {
        e.preventDefault()

        let savedSuccessfully = true

        if (JSON.stringify(profile) !== JSON.stringify(snapshot.profile)) {
            savedSuccessfully = await handleSaveBusinessProfile(e)
        }
        if (savedSuccessfully) {
            setIsEditing(false)
        }
    }

    const renderCallingToggle = (checked, onChange, disabled = false) => (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            className={`${styles.callingSwitch} ${checked ? styles.callingSwitchActive : ""}`}
            onClick={() => !disabled && onChange(!checked)}
            disabled={disabled}
        >
            <span />
        </button>
    )

    const webhookHealthDiagnostic = useMemo(() => {
        if (!webhookHealth?.value) return null
        const value = webhookHealth.value || {}
        const state = `${value.status || value.event || value.state || ""}`.toUpperCase()
        const restricted = ["BANNED", "BLOCKED", "DISABLED", "INELIGIBLE", "RESTRICTED", "SUSPENDED"].some(status => state.includes(status))
        return {
            id: "meta-webhook-health",
            label: restricted ? "A Meta informou uma restrição" : "Atualização recebida da Meta",
            status: restricted ? "warning" : "ok",
            description: `${webhookHealth.field || "Conta"}${state ? `: ${state}` : ""}${webhookHealth.received_at ? ` em ${new Date(webhookHealth.received_at).toLocaleString("pt-BR")}` : ""}.`,
        }
    }, [webhookHealth])

    const healthDiagnostics = useMemo(() => [
        ...(webhookHealthDiagnostic ? [webhookHealthDiagnostic] : []),
        ...(diagnostics.health || []),
    ], [diagnostics.health, webhookHealthDiagnostic])

    const renderPreview = () => (
        <div className={styles.whatsappPreview}>
            <div className={styles.previewTitle}>Prévia no WhatsApp</div>
            <div className={styles.previewHeader}>
                <div className={styles.previewImage}>
                    <img src={getProfilePictureFallback()} alt="Foto do perfil" />
                </div>
                <div className={styles.previewIdentity}>
                    <strong>{(isEditingDisplayName ? displayNameDraft : displayName) || phoneData?.verified_name || "Nome da empresa"}</strong>
                    {usernameData.username && <span>@{usernameData.username}</span>}
                    <span>{maskPhone(phoneData?.display_phone_number || "") || "Número do WhatsApp"}</span>
                    <button type="button" className={styles.previewShareButton}>
                        <MdOutlineShare /> Compartilhar
                    </button>
                </div>
            </div>
            <div className={styles.previewBody}>
                <div className={styles.previewRows}>
                    <p><MdOutlineBusiness /> {getVerticalLabel(profile.vertical)}</p>
                    {profile.description &&
                        <p><MdOutlineInfo /> {profile.description}</p>
                    }
                    {profile.address &&
                        <p><MdOutlinePlace /> {profile.address}</p>
                    }
                    {profile.email &&
                        <p><MdOutlineMail /> {profile.email}</p>
                    }
                    {getPreviewWebsites().map((website, index) => (
                        <p key={`${website}-${index}`}><MdOutlinePublic /> {website}</p>
                    ))}
                </div>
            </div>
            <div className={styles.previewNotice}>Esta experiência pode variar dependendo do dispositivo.</div>
        </div>
    )

    return (
        <div className={styles.numberManager}>
            <FormGroup labelText="Conta do WhatsApp">
                <Select name="Conta do WhatsApp" placeholder="Buscar conta..." options={accountOptions} selected={selectedInboxId} onChange={setSelectedInboxId} disabled={!accountOptions.length} />
            </FormGroup>

            {selectedInboxId && loaded &&
                <div className={styles.profileWorkspace}>
                    <div className={styles.profileEditor}>
                        <div className={styles.profileTopPanel}>
                            <div className={styles.numberStatus}>
                                <div>
                                    <span>Número</span>
                                    <strong>{maskPhone(phoneData?.display_phone_number || "") || "-"}</strong>
                                </div>
                                <div>
                                    <span>Nome atual</span>
                                    <strong>{phoneData?.verified_name || "-"}</strong>
                                </div>
                                <div>
                                    <span>Status do nome</span>
                                    <strong>{getStatusValue(phoneData?.name_status)}</strong>
                                </div>
                                <div>
                                    <span>Qualidade</span>
                                    <strong>{getStatusValue(phoneData?.quality_rating)}</strong>
                                </div>
                                <div>
                                    <span>Conta oficial</span>
                                    <strong>{phoneData?.is_official_business_account ? "Sim" : "Não"}</strong>
                                </div>
                            </div>

                            <div className={styles.officialBox}>
                                <div>
                                    <strong>
                                        Conta Comercial Oficial
                                        <InfoTooltip text={officialBadgeTooltip} preferredPosition="bottom" />
                                    </strong>
                                </div>
                                <div className={styles.officialActions}>
                                    <Button variant="secondary" onClick={() => getPhoneProfile()}>
                                        <MdOutlineRefresh /> Atualizar status
                                    </Button>
                                    <Button variant="primary" onClick={() => openLinkExternal(officialBusinessAccountUrl)}>
                                        <MdOpenInNew /> Abrir solicitação
                                    </Button>
                                </div>
                            </div>

                            <div className={styles.diagnosticsPanel}>
                                <div>
                                    <strong>Pontos de atenção</strong>
                                    <span>Alertas que podem afetar uso, confiança ou aprovação da conta.</span>
                                </div>
                                {healthDiagnostics.length > 0 ?
                                    <div className={styles.diagnosticsGrid}>
                                        {healthDiagnostics.map(renderDiagnosticItem)}
                                    </div>
                                    :
                                    <div className={styles.diagnosticsEmpty}>
                                        <MdOutlineCheckCircle />
                                        <span>Sem alertas relevantes para este número no momento.</span>
                                    </div>
                                }
                            </div>

                            <div className={styles.diagnosticsPanel}>
                                <div>
                                    <strong>
                                        Checklist para conta oficial
                                        <InfoTooltip text="A Oktto não aprova o selo, mas ajuda você a conferir os pontos que costumam pesar antes de abrir a solicitação na Meta." preferredPosition="bottom" />
                                    </strong>
                                    <span>Use esta lista para preparar a solicitação do selo oficial.</span>
                                </div>
                                <div className={styles.diagnosticsGrid}>
                                    {(diagnostics.official_business_account || []).map(renderDiagnosticItem)}
                                </div>
                            </div>
                        </div>

                        <div className={styles.displayNameBox}>
                            <div className={styles.displayNameHeader}>
                                <strong>
                                    Nome de exibição
                                    <InfoTooltip text="É o nome que aparece para seus clientes no WhatsApp. Ao trocar, ele pode levar um tempo para ser aprovado." preferredPosition="bottom" />
                                </strong>
                            </div>
                            {!isEditingDisplayName ?
                                <div className={styles.displayNameView}>
                                    <div>
                                        <span>Nome atual</span>
                                        <strong>{displayName || "-"}</strong>
                                    </div>
                                    <Button variant="secondary" type="button" onClick={handleStartEditingDisplayName} disabled={isEditing || isEditingUsername || isEditingProfilePicture || isEditingCallingSettings}>
                                        <MdEdit /> Alterar nome
                                    </Button>
                                </div>
                                :
                                <form className={styles.displayNameForm} onSubmit={handleSaveDisplayName}>
                                    <FormGroup labelText="Novo nome de exibição" rightContent={<CharacterCounter value={displayNameDraft} limit={75} />}>
                                        <Input placeholder="Nome que aparece no WhatsApp" value={displayNameDraft} onChange={(e) => setDisplayNameDraft(e.target.value)} required={true} maxLength={75} />
                                    </FormGroup>
                                    <div>
                                        <Button variant="primary" type="submit">Enviar para aprovação</Button>
                                        <Button variant="secondary" type="button" onClick={handleCancelEditingDisplayName}>Cancelar</Button>
                                    </div>
                                </form>
                            }
                        </div>

                        <div className={styles.displayNameBox}>
                            <div className={styles.profileToolbar}>
                                <div>
                                    <strong>
                                        Nome de usuário do WhatsApp
                                        <InfoTooltip text="Identificador único do número, como @suaempresa. Clientes poderão encontrar a empresa pelo nome exato. O número comercial continua visível no perfil." preferredPosition="bottom" />
                                    </strong>
                                    <span>Um nome de usuário pode pertencer a apenas um número em todo o WhatsApp.</span>
                                </div>
                                <div>
                                    <Button variant="secondary" type="button" onClick={() => getBusinessUsername(selectedInboxId, true)}>
                                        <MdOutlineRefresh /> Atualizar
                                    </Button>
                                    {!isEditingUsername && !usernameError &&
                                        <Button variant="secondary" type="button" onClick={() => {
                                            setUsernameDraft(usernameData.requested_username || usernameData.username || "")
                                            setIsEditingUsername(true)
                                        }} disabled={isEditing || isEditingDisplayName || isEditingProfilePicture || isEditingCallingSettings}>
                                            <MdEdit /> {usernameData.username || usernameData.requested_username ? "Alterar" : "Criar"}
                                        </Button>
                                    }
                                </div>
                            </div>

                            {usernameError ?
                                <div className={styles.usernameUnavailable}>
                                    <MdOutlineWarningAmber />
                                    <span>{usernameError}</span>
                                </div>
                                : !isEditingUsername ?
                                    <div className={styles.usernameSummary}>
                                        <div>
                                            <span>Nome atual</span>
                                            <strong>{usernameData.username ? `@${usernameData.username}` : "Nenhum"}</strong>
                                        </div>
                                        {usernameData.requested_username && usernameData.requested_username !== usernameData.username &&
                                            <div>
                                                <span>Solicitação</span>
                                                <strong>@{usernameData.requested_username}</strong>
                                            </div>
                                        }
                                        <div>
                                            <span>Status</span>
                                            <strong>{getUsernameStatusLabel(usernameData.status)}</strong>
                                        </div>
                                        {usernameData.username &&
                                            <Button variant="secondary" type="button" onClick={() => setShowRemoveUsernameConfirm(true)}>
                                                <MdOutlineDeleteOutline /> Remover
                                            </Button>
                                        }
                                    </div>
                                    :
                                    <form className={styles.displayNameForm} onSubmit={handleSaveUsername}>
                                        {usernameSuggestions.length > 0 &&
                                            <div className={styles.usernameSuggestions}>
                                                <span>{metaUsernameSuggestions.length ? "Sugestões reservadas pela Meta" : "Sugestões geradas pelo nome comercial"}</span>
                                                {!metaUsernameSuggestions.length && <small>A disponibilidade será confirmada pela Meta somente após o envio.</small>}
                                                <div>
                                                    {usernameSuggestions.map(suggestion => (
                                                        <button key={suggestion} type="button" onClick={() => setUsernameDraft(suggestion)}>@{suggestion}</button>
                                                    ))}
                                                </div>
                                            </div>
                                        }
                                        <FormGroup labelText="Nome de usuário" rightContent={<CharacterCounter value={usernameDraft} limit={35} />}>
                                            <div className={styles.usernameInput}>
                                                <span aria-hidden="true">@</span>
                                                <Input placeholder="suaempresa" value={usernameDraft} onChange={(e) => setUsernameDraft(normalizeUsername(e.target.value))} required={true} minLength={3} maxLength={35} />
                                            </div>
                                        </FormGroup>
                                        <span>Use de 3 a 35 caracteres. Espaços viram “_”; acentos e símbolos não permitidos são removidos automaticamente.</span>
                                        <div>
                                            <Button variant="primary" type="submit" disabled={!isValidUsername(usernameDraft)}>Enviar para a Meta</Button>
                                            <Button variant="secondary" type="button" onClick={() => setIsEditingUsername(false)}>Cancelar</Button>
                                        </div>
                                    </form>
                            }
                        </div>

                        <div className={styles.displayNameBox}>
                            <div className={styles.profileToolbar}>
                                <div>
                                    <strong>
                                        Foto de perfil
                                        <InfoTooltip text="Imagem exibida no perfil do WhatsApp. Use uma versão clara do logotipo ou da marca." preferredPosition="bottom" />
                                    </strong>
                                </div>
                                <div>
                                    {!isEditingProfilePicture &&
                                        <Button variant="secondary" type="button" onClick={handleStartEditingProfilePicture} disabled={isEditing || isEditingDisplayName || isEditingUsername || isEditingCallingSettings}>
                                            <MdEdit /> Alterar foto
                                        </Button>
                                    }
                                </div>
                            </div>
                            {!isEditingProfilePicture ?
                                <div className={styles.profilePictureCard}>
                                    <div className={styles.profilePictureThumb}>
                                        <img src={getProfilePictureFallback()} alt="Foto do perfil" />
                                    </div>
                                    <div className={styles.profilePictureSummary}>
                                        <strong>{profile.profile_picture_url ? "Foto personalizada" : "Sem foto personalizada"}</strong>
                                        <span>{profile.profile_picture_url ? "Essa imagem aparece para os clientes no WhatsApp." : "O perfil aparece com uma foto em branco."}</span>
                                    </div>
                                </div>
                                :
                                <form className={styles.profilePictureEditForm} onSubmit={handleSubmitProfilePicture}>
                                    <div className={styles.profilePictureEditGrid}>
                                        <div className={styles.profilePicturePreviewPanel}>
                                            <span>Prévia</span>
                                            <div className={styles.profilePicturePreviewLarge}>
                                                <img src={getProfilePictureFallback()} alt="Prévia da foto do perfil" />
                                            </div>
                                            <small>{profilePicture?.[0] ? "Nova imagem selecionada" : "Imagem atual"}</small>
                                        </div>
                                        <div className={styles.profilePictureUploadPanel}>
                                            <FormGroup labelText="Nova foto">
                                                <InputFile value={profilePicture} onChange={setProfilePicture} accept="image/png,image/jpeg,image/jpg" disabled={!isEditingProfilePicture} />
                                            </FormGroup>
                                            <span>Use uma imagem quadrada, clara e fácil de reconhecer.</span>
                                        </div>
                                    </div>
                                    <div className={styles.profilePictureEditActions}>
                                        <Button variant="primary" type="submit">Salvar foto</Button>
                                        {profile.profile_picture_url &&
                                            <Button variant="secondary" type="button" onClick={handleOpenRemoveProfilePicture}>
                                                <MdOutlineDeleteOutline /> Remover foto
                                            </Button>
                                        }
                                        <Button variant="secondary" type="button" onClick={handleCancelEditingProfilePicture}>Cancelar</Button>
                                    </div>
                                </form>
                            }
                        </div>

                        <div className={`${styles.businessInfoBox} ${styles.callingConfigBox}`}>
                            <div className={styles.profileToolbar}>
                                <div>
                                    <strong>
                                        <MdCall /> Ligações de voz
                                        <InfoTooltip text="Configura diretamente as opções de ligação deste número na WhatsApp Cloud API." preferredPosition="bottom" />
                                    </strong>
                                    <span>As mudanças são aplicadas pela Meta ao número selecionado.</span>
                                </div>
                                <div>
                                    <Button variant="secondary" type="button" onClick={() => getCallingSettings(selectedInboxId, true)}>
                                        <MdOutlineRefresh /> Atualizar
                                    </Button>
                                    {!isEditingCallingSettings ?
                                        <Button variant="secondary" type="button" onClick={() => setIsEditingCallingSettings(true)} disabled={Boolean(callingSettingsError) || callingSettings.sip?.status === "ENABLED" || isEditing || isEditingDisplayName || isEditingUsername || isEditingProfilePicture}>
                                            <MdEdit /> Configurar
                                        </Button>
                                        :
                                        <>
                                            <Button variant="primary" type="button" onClick={handleSaveCallingSettings}>Salvar</Button>
                                            <Button variant="secondary" type="button" onClick={handleCancelCallingSettings}>Cancelar</Button>
                                        </>
                                    }
                                </div>
                            </div>

                            {!callingSettingsError &&
                                <div className={`${styles.callingProtocolCard} ${callingSettings.sip?.status === "ENABLED" ? styles.callingProtocolCardWarning : ""}`}>
                                    <div>
                                        <strong>Tipo de conexão</strong>
                                        <span>{callingSettings.sip?.status === "ENABLED"
                                            ? "SIP foi detectado neste número e não é compatível com as chamadas no chat da Oktto."
                                            : "WebRTC com sinalização pela Graph API e pelo webhook de chamadas da Meta."}</span>
                                        {callingSettings.sip?.status === "ENABLED" &&
                                            <ol className={styles.callingProtocolInstructions}>
                                                <li>Abra este número no Gerenciador do WhatsApp.</li>
                                                <li>Nas configurações de chamadas, desative “Usar Protocolo de Iniciação de Sessão (SIP)”.</li>
                                                <li>Salve na Meta e clique em “Verificar novamente” nesta tela.</li>
                                            </ol>
                                        }
                                    </div>
                                    <div className={styles.callingProtocolStatus}>
                                        <span className={`${styles.protocolBadge} ${callingSettings.sip?.status === "ENABLED" ? styles.protocolBadgeWarning : ""}`}>
                                            {callingSettings.sip?.status === "ENABLED" ? "SIP incompatível" : "WebRTC + Webhooks"}
                                        </span>
                                        {callingSettings.sip?.status === "ENABLED" &&
                                            <div className={styles.callingProtocolActions}>
                                                <Button variant="primary" type="button" onClick={() => openLinkExternal(officialBusinessAccountUrl)} disabled={!officialBusinessAccountUrl}>
                                                    <MdOpenInNew /> Alterar para WebRTC na Meta
                                                </Button>
                                                <Button variant="secondary" type="button" onClick={() => getCallingSettings(selectedInboxId, true)}>
                                                    <MdOutlineRefresh /> Verificar novamente
                                                </Button>
                                            </div>
                                        }
                                    </div>
                                </div>
                            }

                            {callingSettingsError ?
                                <div className={styles.callingUnavailable}>
                                    <MdOutlineWarningAmber />
                                    <div>
                                        <strong>Recurso indisponível</strong>
                                        <span>{callingSettingsError}</span>
                                    </div>
                                </div>
                                :
                                <div className={styles.callingSettings}>
                                    <div className={styles.callingSettingRow}>
                                        <div>
                                            <strong>Permitir ligações de voz</strong>
                                            <span>Permite fazer, receber e solicitar ligações pelo número comercial.</span>
                                        </div>
                                        {renderCallingToggle(callingSettings.status === "ENABLED", checked => handleChangeCallingSetting("status", checked ? "ENABLED" : "DISABLED"), !isEditingCallingSettings)}
                                    </div>

                                    <div className={styles.callingSection}>
                                        <div className={styles.callingSectionTitle}>Configurações de ligação recebida</div>
                                        <div className={styles.callingSettingRow}>
                                            <div>
                                                <strong>Exibir botões de ligação</strong>
                                                <span>Ocultar o botão não impede chamadas originadas por mensagens, templates ou links com botão de ligação.</span>
                                            </div>
                                            {renderCallingToggle(callingSettings.call_icon_visibility === "DEFAULT", checked => handleChangeCallingSetting("call_icon_visibility", checked ? "DEFAULT" : "DISABLE_ALL"), !isEditingCallingSettings || callingSettings.status !== "ENABLED")}
                                        </div>

                                        <div className={styles.callingSettingRow}>
                                            <div>
                                                <strong>Definir horário disponível para ligações</strong>
                                                <span>Sem um horário definido, o número fica disponível 24 horas por dia.</span>
                                            </div>
                                            {renderCallingToggle(callingSettings.call_hours.status === "ENABLED", checked => handleChangeCallHours("status", checked ? "ENABLED" : "DISABLED"), !isEditingCallingSettings || callingSettings.status !== "ENABLED")}
                                        </div>

                                        {callingSettings.call_hours.status === "ENABLED" &&
                                            <div className={styles.callHoursEditor}>
                                                <label className={styles.callingField}>
                                                    <span>Fuso horário</span>
                                                    <Input type="text" value={callingSettings.call_hours.timezone_id || ""} onChange={event => handleChangeCallHours("timezone_id", event.target.value)} placeholder="America/Sao_Paulo" disabled={!isEditingCallingSettings} />
                                                </label>

                                                <div className={styles.weeklyHours}>
                                                    {weekDays.map(day => {
                                                        const entries = (callingSettings.call_hours.weekly_operating_hours || []).map((item, index) => ({ item, index })).filter(entry => entry.item.day_of_week === day.id)
                                                        return (
                                                            <div key={day.id} className={styles.weeklyDay}>
                                                                <div className={styles.weeklyDayHeader}>
                                                                    <strong>{day.label}</strong>
                                                                    {isEditingCallingSettings && entries.length < 2 &&
                                                                        <Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={() => handleAddWeeklyHours(day.id)}><MdAdd /> Adicionar horário</Button>
                                                                    }
                                                                </div>
                                                                {entries.length ? entries.map(({ item, index }) => (
                                                                    <div key={`${day.id}-${index}`} className={styles.callingTimeRow}>
                                                                        <Input type="time" value={item.open_time || "08:00"} onChange={event => handleChangeWeeklyHours(index, "open_time", event.target.value)} disabled={!isEditingCallingSettings} />
                                                                        <span>até</span>
                                                                        <Input type="time" value={item.close_time || "18:00"} onChange={event => handleChangeWeeklyHours(index, "close_time", event.target.value)} disabled={!isEditingCallingSettings} />
                                                                        {isEditingCallingSettings && <Button type="button" variant="secondary" size="sm" fullWidth={false} title="Remover horário" aria-label="Remover horário" onClick={() => handleRemoveWeeklyHours(index)}><MdOutlineDeleteOutline /></Button>}
                                                                    </div>
                                                                )) : <span className={styles.callingEmptyValue}>Fechado</span>}
                                                            </div>
                                                        )
                                                    })}
                                                </div>

                                                <div className={styles.holidayHeader}>
                                                    <div>
                                                        <strong>Interromper temporariamente as ligações recebidas</strong>
                                                        <span>Cadastre feriados, eventos ou intervalos especiais de indisponibilidade.</span>
                                                    </div>
                                                    {isEditingCallingSettings && (callingSettings.call_hours.holiday_schedule || []).length < 20 &&
                                                        <Button variant="secondary" type="button" onClick={handleAddHolidaySchedule}><MdAdd /> Adicionar interrupção</Button>
                                                    }
                                                </div>
                                                <div className={styles.holidayList}>
                                                    {(callingSettings.call_hours.holiday_schedule || []).map((item, index) => (
                                                        <div key={`${item.date}-${index}`} className={styles.holidayRow}>
                                                            <Input type="date" min={new Date().toISOString().slice(0, 10)} value={item.date || ""} onChange={event => handleChangeHolidaySchedule(index, "date", event.target.value)} disabled={!isEditingCallingSettings} />
                                                            <Input type="time" value={item.start_time || "00:00"} onChange={event => handleChangeHolidaySchedule(index, "start_time", event.target.value)} disabled={!isEditingCallingSettings} />
                                                            <span>até</span>
                                                            <Input type="time" value={item.end_time || "23:59"} onChange={event => handleChangeHolidaySchedule(index, "end_time", event.target.value)} disabled={!isEditingCallingSettings} />
                                                            {isEditingCallingSettings && <Button type="button" variant="secondary" size="sm" fullWidth={false} title="Remover interrupção" aria-label="Remover interrupção" onClick={() => handleRemoveHolidaySchedule(index)}><MdOutlineDeleteOutline /></Button>}
                                                        </div>
                                                    ))}
                                                    {!(callingSettings.call_hours.holiday_schedule || []).length && <span className={styles.callingEmptyValue}>Nenhuma interrupção programada.</span>}
                                                </div>
                                            </div>
                                        }
                                    </div>

                                    <div className={styles.callingSection}>
                                        <div className={styles.callingSectionTitle}>Configurações de retorno de ligação</div>
                                        <div className={styles.callingSettingRow}>
                                            <div>
                                                <strong>Permitir retornos de ligação</strong>
                                                <span>Após uma chamada atendida ou perdida, o WhatsApp pode pedir ao cliente permissão para a empresa retornar.</span>
                                            </div>
                                            {renderCallingToggle(callingSettings.callback_permission_status === "ENABLED", checked => handleChangeCallingSetting("callback_permission_status", checked ? "ENABLED" : "DISABLED"), !isEditingCallingSettings || callingSettings.status !== "ENABLED")}
                                        </div>
                                    </div>

                                    {(callingSettings.restrictions?.restrictions_list || []).length > 0 &&
                                        <div className={styles.callingRestrictions}>
                                            <MdOutlineWarningAmber />
                                            <div>
                                                <strong>A Meta aplicou restrições a este número</strong>
                                                {(callingSettings.restrictions.restrictions_list || []).map((restriction, index) => <span key={`${restriction.type}-${index}`}>{restriction.reason || restriction.type}</span>)}
                                            </div>
                                        </div>
                                    }

                                    <span className={styles.callingPropagationNote}>A Meta aplica as regras no servidor imediatamente, mas alguns clientes do WhatsApp podem levar alguns dias para atualizar os botões exibidos.</span>
                                </div>
                            }
                        </div>

                        <div className={styles.businessInfoBox}>
                            <div className={styles.profileToolbar}>
                                <div>
                                    <strong>
                                        Informações da empresa
                                        <InfoTooltip text={isEditing ? "Use a prévia ao lado para conferir como o perfil vai ficar antes de salvar." : "Clique em editar quando quiser alterar os dados. Mantemos tudo bloqueado para evitar mudanças sem querer."} preferredPosition="bottom" />
                                    </strong>
                                </div>
                                <div>
                                    {!isEditing ?
                                        <Button variant="secondary" type="button" onClick={handleStartEditing} disabled={isEditingDisplayName || isEditingUsername || isEditingProfilePicture || isEditingCallingSettings}>
                                            <MdEdit /> Editar perfil
                                        </Button>
                                        :
                                        <>
                                            <Button variant="primary" type="button" onClick={handleSaveAll}>Salvar alterações</Button>
                                            <Button variant="secondary" type="button" onClick={handleCancelEditing}>Cancelar</Button>
                                        </>
                                    }
                                </div>
                            </div>

                            <form className={styles.numberForm} onSubmit={handleSaveAll}>
                                <FormGroup labelText={labelWithInfo("Categoria", "Ajuda seus clientes a entenderem rapidamente o tipo de negócio.")}>
                                    <Select name="Categoria" options={verticalOptions} selected={profile.vertical || "OTHER"} onChange={(value) => handleChangeProfile("vertical", value || "OTHER")} allowDeselectAll={false} disabled={!isEditing} />
                                </FormGroup>
                                <FormGroup labelText={labelWithInfo(getOptionalLabel("Descrição"), "Um resumo curto sobre a empresa, produtos ou atendimento.")} rightContent={<CharacterCounter value={profile.description} limit={512} />}>
                                    <Textarea placeholder="Descrição..." value={profile.description || ""} onChange={(e) => handleChangeProfile("description", e.target.value)} rows={4} maxLength={512} disabled={!isEditing} />
                                </FormGroup>
                                <FormGroup labelText={labelWithInfo(getOptionalLabel("Endereço"), "Mostre onde sua empresa atende ou onde fica o ponto físico.")}>
                                    <Input placeholder="Endereço..." value={profile.address || ""} onChange={(e) => handleChangeProfile("address", e.target.value)} disabled={!isEditing} />
                                </FormGroup>
                                <FormGroup labelText={labelWithInfo(getOptionalLabel("Email"), "Canal de contato comercial que pode aparecer no perfil.")} rightContent={<CharacterCounter value={profile.email} limit={128} />}>
                                    <Input type="email" placeholder="Insira o email comercial" value={profile.email || ""} onChange={(e) => handleChangeProfile("email", e.target.value)} maxLength={128} disabled={!isEditing} />
                                </FormGroup>
                                <div className={styles.websitesFields}>
                                    <FormGroupInline>
                                        <FormGroup labelText={labelWithInfo(getOptionalLabel("Site:"), "Link principal da empresa, como site, loja ou página institucional.")} rightContent={<CharacterCounter value={profile.websites?.[0]} limit={256} />}>
                                            <Input type="url" placeholder="https://www.exemplo.com" value={profile.websites?.[0] || ""} onChange={(e) => handleChangeWebsite(0, e.target.value)} maxLength={256} disabled={!isEditing} />
                                        </FormGroup>
                                        <FormGroup labelText={labelWithInfo(getOptionalLabel("Site:"), "Use um segundo link se tiver outra página importante para o cliente.")} rightContent={<CharacterCounter value={profile.websites?.[1]} limit={256} />}>
                                            <Input type="url" placeholder="https://www.exemplo.com" value={profile.websites?.[1] || ""} onChange={(e) => handleChangeWebsite(1, e.target.value)} maxLength={256} disabled={!isEditing} />
                                        </FormGroup>
                                    </FormGroupInline>
                                </div>
                            </form>
                        </div>

                        <div className={`${styles.businessInfoBox} ${styles.profileHistoryBox}`}>
                            <div className={styles.profileToolbar}>
                                <div>
                                    <strong>
                                        Histórico de alterações
                                        <InfoTooltip text="Mostra as últimas mudanças feitas no perfil do número dentro da Oktto." preferredPosition="bottom" />
                                    </strong>
                                </div>
                                <div>
                                    <Button variant="secondary" type="button" onClick={() => getPhoneProfile()}>
                                        <MdOutlineRefresh /> Atualizar
                                    </Button>
                                </div>
                            </div>
                            <div className={styles.historyList}>
                                {webhookLastReceivedAt &&
                                    <div className={styles.historyItem}>
                                        <MdOutlineHistory />
                                        <div>
                                            <strong>Última sincronização automática da Meta</strong>
                                            <span>{new Date(webhookLastReceivedAt).toLocaleString("pt-BR")}</span>
                                        </div>
                                    </div>
                                }
                                {history.length > 0 ? history.map((item, index) => (
                                    <div key={`${item.created_at}-${index}`} className={styles.historyItem}>
                                        <MdOutlineHistory />
                                        <div>
                                            <strong>{item.title}</strong>
                                            <span>{item.user_name || "Usuário"} em {item.created_at ? new Date(item.created_at).toLocaleString("pt-BR") : "-"}</span>
                                        </div>
                                    </div>
                                )) : <span>Nenhuma alteração registrada até o momento.</span>}
                            </div>
                        </div>
                    </div>
                    {renderPreview()}
                </div>
            }

            {showRemovePictureConfirm &&
                <ModalConfirm
                    closeModal={() => setShowRemovePictureConfirm(false)}
                    icon={<MdOutlineDeleteOutline />}
                    title="Remover foto de perfil"
                    description="Vamos substituir a foto atual por uma foto em branco."
                    text="Depois disso, o perfil deixa de exibir uma imagem personalizada para os clientes."
                    onSubmit={handleConfirmRemoveProfilePicture}
                />
            }

            {showRemoveUsernameConfirm &&
                <ModalConfirm
                    closeModal={() => setShowRemoveUsernameConfirm(false)}
                    icon={<MdOutlineDeleteOutline />}
                    title="Remover nome de usuário"
                    description={`Deseja remover @${usernameData.username} deste número?`}
                    text="O nome poderá ficar disponível para outra pessoa ou empresa. Esta ação não desconecta o número."
                    onSubmit={handleConfirmRemoveUsername}
                />
            }

            {!linkedAccounts.length && <span>Nenhuma conta conectada até o momento.</span>}
        </div>
    )
}

export default PhoneNumberManager
